// Editorial data for the report guide. Prices are provider list prices as last noted by us and
// change often — the UI says so and every row sends people to the provider to confirm.

export const PROVIDERS = [
  {
    key: 'carfax', name: 'Carfax', price: '$44.99', priceNote: '3 for $64.99',
    bestFor: 'One car you are seriously going to buy',
    strengths: ['Service records', 'Accidents', 'Title'],
    skipIf: 'Skip if you are comparing many cars — the cost adds up fast.',
    url: 'https://www.carfax.com/vehicle-history/get-vehicle-history-report',
  },
  {
    key: 'autocheck', name: 'AutoCheck', price: '$24.99', priceNote: '25 for $49.99',
    bestFor: 'Shopping several cars, or ex-auction stock',
    strengths: ['Auction data', 'Comparison score', 'Title'],
    skipIf: 'Skip if maintenance history is your top priority.',
    url: 'https://www.autocheck.com/vehiclehistory/autocheck/en/order',
  },
  {
    key: 'vinaudit', name: 'VinAudit', price: '$9.99', priceNote: 'unlimited $24.99/mo',
    bestFor: 'Title brands and theft checks on a budget',
    strengths: ['NMVTIS title data', 'Market value', 'Theft'],
    skipIf: 'Skip if you need service records.',
    url: 'https://www.vinaudit.com/free-vin-check',
  },
  {
    key: 'bumper', name: 'Bumper', price: '$1', priceNote: 'trial, then $19.99/mo',
    bestFor: 'Browsing many listings over a month',
    strengths: ['Listing history', 'Photos', 'Recalls'],
    skipIf: 'Skip if you only need one report — it renews monthly until you cancel.',
    url: 'https://bumper.com/',
  },
  {
    key: 'clearvin', name: 'ClearVin', price: '$14.99', priceNote: '5 for $34.99',
    bestFor: 'Salvage and rebuilt-title inspection',
    strengths: ['NMVTIS title data', 'Auction photos', 'Salvage'],
    skipIf: 'Skip if you are checking a clean-title dealer car.',
    url: 'https://www.clearvin.com/',
  },
  {
    key: 'carvert', name: 'carVertical', price: '$29.99', priceNote: '3 for $54.99',
    bestFor: 'Cars imported from or sold to Europe',
    strengths: ['EU + US records', 'Damage photos', 'Imports'],
    skipIf: 'Skip if the car never left the US.',
    url: 'https://www.carvertical.com/',
  },
  {
    key: 'epicvin', name: 'EpicVIN', price: '$4.99', priceNote: '10 for $29.99',
    bestFor: 'A fast sanity check before a test drive',
    strengths: ['Title brands', 'Theft', 'Basic specs'],
    skipIf: 'Skip once you are about to pay — it is a screening check, not a full history.',
    url: 'https://www.epicvin.com/',
  },
  {
    key: 'nicb', name: 'NICB VINCheck', price: 'Free', priceNote: '5 lookups per day',
    bestFor: 'Theft and total-loss flags only',
    strengths: ['Theft flag', 'Total-loss flag'],
    skipIf: 'Skip if you need accidents or ownership history.',
    url: 'https://www.nicb.org/vincheck',
    free: true,
  },
];

export const byKey = Object.fromEntries(PROVIDERS.map((p) => [p.key, p]));

export const PICKS = [
  {
    key: 'carfax', label: 'Best for one car',
    why: 'The deepest service and maintenance record of the group, because independent repair shops report into its network. The one to pay for once you have decided on a specific car.',
  },
  {
    key: 'autocheck', label: 'Best for comparing cars',
    why: 'The multi-report pack brings the cost per VIN below $2, and its auction feed is the reason to choose it for ex-fleet and ex-auction cars.',
  },
  {
    key: 'vinaudit', label: 'Best on a budget',
    why: 'NMVTIS-approved, so the salvage, flood and theft layer is the same federal data the expensive reports draw on — at a fraction of the price.',
  },
];

// ---- Quiz -------------------------------------------------------------------------------------

export const QUIZ = [
  { key: 'type', question: 'What are you buying?', options: [['dealer', 'A used car from a dealer'], ['private', 'A private-party sale'], ['salvage', 'An auction or salvage car'], ['import', 'An imported vehicle']] },
  { key: 'use', question: 'Is this for personal or professional use?', options: [['personal', 'Personal — I am buying for myself'], ['pro', 'Professional — I buy and sell cars']] },
  { key: 'volume', question: 'How many VINs will you check?', options: [['one', 'Just one'], ['few', '2 to 5'], ['many', 'Dozens']] },
  { key: 'priority', question: 'What matters most?', options: [['price', 'The cheapest report that does the job'], ['balanced', 'A balance of price and detail'], ['depth', 'The most complete history']] },
  { key: 'budget', question: 'What is your budget for this check?', options: [['low', 'Under $15'], ['mid', '$15 – $50'], ['high', 'Whatever it takes']] },
];

const WHY = {
  carfax: 'You are buying one car you intend to keep, so completeness matters more than the price of the report. Carfax has the deepest independent-shop service records, which is what exposes deferred maintenance and unreported bodywork on a dealer car.',
  autocheck: 'You are looking at several vehicles, so cost per VIN is the number that matters. AutoCheck\'s multi-report pack drops well under $2 a VIN, and its auction feed is the history that follows ex-fleet and ex-auction stock.',
  vinaudit: 'Your budget is tight and your real risk is a bad title, not a missing oil change. VinAudit is NMVTIS-approved, so the salvage, flood and theft layer is the same federal data the pricier reports use.',
  clearvin: 'Auction and salvage cars are judged on what you can see. ClearVin pairs NMVTIS title data with archived Copart and IAAI photographs, so you can look at the damage instead of trusting a description.',
  carvert: 'Imported vehicles lose their history at the border. carVertical combines European records with US ones, which is where mileage and damage events tend to disappear after re-registration.',
  bumper: 'You will be checking listings on and off for weeks. A monthly plan beats per-report pricing once you pass roughly four VINs, and listing history shows how long a car has been on the market.',
  epicvin: 'You just need a quick red-flag check before you drive out to see a car. EpicVIN covers title brands and theft for the price of a coffee — run our free recall check alongside it and only pay for a full report on the car you shortlist.',
};

const ALT = {
  carfax: 'AutoCheck if you end up comparing more than two cars.',
  autocheck: 'Carfax on the single car you decide to buy, for the service-record layer.',
  vinaudit: 'AutoCheck if you shortlist more than three cars.',
  clearvin: 'carVertical if the vehicle has any overseas history.',
  carvert: 'ClearVin for US auction photographs on the same VIN.',
  bumper: 'AutoCheck\'s 25-report pack if you prefer no recurring charge.',
  epicvin: 'VinAudit for a slightly deeper title and value picture.',
};

const SKIP = {
  carfax: 'You probably do not need a second full report until you are down to one car.',
  autocheck: 'You do not need Carfax for every car on the list — save it for the finalist.',
  vinaudit: 'You do not need a premium report for a first-pass screen.',
  clearvin: 'You do not need service-record reports for a car that is being sold on its damage.',
  carvert: 'You do not need a US-only report — it will miss the overseas years.',
  bumper: 'You do not need to pay per report if you will look at many cars.',
  epicvin: 'You do not need a full history until you are ready to make an offer.',
};

export function recommend(a) {
  if (Object.keys(a).length < QUIZ.length) return null;
  let key;
  if (a.type === 'salvage') key = 'clearvin';
  else if (a.type === 'import') key = 'carvert';
  else if (a.use === 'pro' || a.volume === 'many') key = 'autocheck';
  else if (a.budget === 'low') key = a.priority === 'price' && a.volume === 'one' ? 'epicvin' : 'vinaudit';
  else if (a.priority === 'depth' && a.budget === 'high') key = 'carfax';
  else if (a.volume === 'few' && a.priority !== 'depth') key = 'autocheck';
  else if (a.priority === 'price') key = 'autocheck';
  else key = 'carfax';
  const p = byKey[key];
  return { key, provider: p, why: WHY[key], alt: ALT[key], skip: SKIP[key] };
}

// ---- FAQ --------------------------------------------------------------------------------------

export const FAQS = [
  { q: 'Carfax vs AutoCheck: which one should I buy?', a: "Carfax is stronger on service and maintenance records because independent shops report into its network; AutoCheck is stronger on auction and wholesale history and gives you a single comparative score. If you're buying one car from a dealer, Carfax. If you're comparing many cars or looking at ex-auction stock, AutoCheck's multi-report pack costs less per VIN. On a car you're serious about, running both can be money well spent against a five-figure mistake." },
  { q: 'Are vehicle history reports reliable?', a: 'Reliable for what they contain, incomplete by design. A report can only show events that someone reported: a crash settled in cash between two drivers never enters any database. Treat a clean report as an absence of red flags, not proof of a perfect car — it never replaces a pre-purchase inspection by a mechanic.' },
  { q: 'Is there a genuinely free way to check a VIN?', a: "Yes, for part of the picture. NHTSA gives recall campaigns, owner complaints and crash-test ratings for free (that's what our checker shows), vPIC decodes the VIN's specifications, and NICB VINCheck flags theft and insurer total-loss records. What none of them give you is accident history, ownership count, odometer readings or service records." },
  { q: 'What is NMVTIS and why does it keep coming up?', a: "The National Motor Vehicle Title Information System is a federal database that states, insurers and salvage yards are required to report into. Approved providers can resell access to it, which is why some cheap reports can still be trustworthy on title brands, salvage and theft. Everything beyond that layer is each vendor's own private data." },
  { q: 'The dealer gave me a free Carfax — is that enough?', a: "A dealer-supplied report is usually real, but it can be weeks old and it's the one report the dealer chose to show you. Check the run date at the top, verify the VIN matches the dash plate and the door-jamb sticker, and consider a second opinion from another provider." },
  { q: 'How do I spot a flood-damaged car?', a: 'Look for a title that moved states shortly after a major storm, an insurance total-loss entry followed by a clean re-title, and gaps in registration history. Reports that combine NMVTIS with auction photos are the most useful here because you can see the car as it sat in the yard. Then check under the carpet and inside the seat rails yourself.' },
  { q: 'Does an open recall mean the car is unsafe to buy?', a: "Not necessarily. Franchised dealers perform recall work free of charge regardless of who owns the car, so an unrepaired recall is leverage on price and a reason to book the appointment early. Airbag and fuel-system campaigns are the ones worth fixing before daily use. To see whether a specific car still has an unrepaired recall, enter its VIN on NHTSA's own recall lookup." },
  { q: 'Why does the checker show model-year numbers instead of "this car\'s recalls"?', a: "NHTSA's public data is organised by make, model and year. It tells you which recall campaigns and complaints exist for that model year — not whether one particular car has already had the repair done. That is why we point you to NHTSA's official VIN lookup for that last step." },
  { q: 'How does CompareVIN make money?', a: "Some outbound links are affiliate links: if you buy a report after clicking, we may earn a commission at no extra cost to you. Our free tools stay free and need no account. See How we work for the full policy." },
];
